import { Alert, CircularProgress, Container, Stack, Typography } from "@mui/material";
import { useActiveLegalDocument } from "src/hooks/useActiveLegalDocument";
import { usePageMeta } from "src/hooks/usePageMeta";
import LegalMarkdown from "src/components/legal/LegalMarkdown";
import { useT } from "src/i18n";

/**
 * The page scaffold shared by /kayttoehdot and /tietosuoja — Container + heading +
 * version label + the three fetch states. Previously duplicated as Kayttoehdot.jsx
 * and Tietosuoja.jsx, which were 50 lines each and differed at 7 (the doc type, the
 * route, and the copy keys). Same move LegalMarkdown records one layer down, applied
 * to the surrounding page.
 *
 * Copy arrives as RESOLVED STRINGS, deliberately — NOT as an i18n prefix this
 * component interpolates. scripts/audit/i18n-keys.cjs collects keys by Babel AST and
 * accepts only `arg.type === "StringLiteral"`, so a template-literal key would not
 * fail CI — it would silently drop the legal copy out of audit coverage, on exactly
 * the text where a missing translation matters most. Passing strings keeps every
 * t() call a literal at its call site.
 */
export default function LegalDocumentPage({
    docType, path, title, description, heading, loadFailedMessage,
}) {
    const t = useT();
    usePageMeta({ title, description, path });
    const { doc, status } = useActiveLegalDocument(docType);

    return (
        <Container maxWidth="md" sx={{ py: { xs: 3, md: 6 } }}>
            <Typography
                variant="h3"
                component="h1"
                sx={{ fontFamily: "'Black Ops One', serif", letterSpacing: "0.04em", mb: 0.5 }}
            >
                {heading}
            </Typography>
            {status === "ready" && doc && (
                <Typography variant="caption" sx={{ color: "text.secondary", letterSpacing: "0.04em" }}>
                    {t("legal.shared.versionLabel", { version: doc.version })}
                    {doc.effectiveDate ? t("legal.shared.effectiveDateSuffix", { date: doc.effectiveDate.slice(0, 10) }) : null}
                </Typography>
            )}
            {/* useFlexGap matters here. LegalMarkdown renders its h2/p/ul blocks as DIRECT
                DOM children of this Stack, and MUI 9 defaults useFlexGap to false — which
                implements spacing as sibling margins PLUS a `margin: 0` reset on every
                child. That reset silently overrode LegalMarkdown's own rhythm (h2 mt:4,
                p mb:1.5), so every block rendered a uniform 16px apart and a heading
                opened no section break. Real `gap` leaves children's margins alone. */}
            <Stack useFlexGap spacing={2} sx={{ mt: 5 }}>
                {status === "loading" && (
                    <Stack spacing={2} sx={{ alignItems: "center", py: 4 }}>
                        <CircularProgress size={28} />
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>{t("common.loading")}</Typography>
                    </Stack>
                )}
                {status === "error" && <Alert severity="error">{loadFailedMessage}</Alert>}
                {/* `&& doc` is not redundant: a 200 with a literal null body would make
                    doc.markdownContent throw. */}
                {status === "ready" && doc && <LegalMarkdown markdown={doc.markdownContent} />}
            </Stack>
        </Container>
    );
}
